import { Box, Text, VStack, HStack, Alert, AlertIcon, Tag } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { StatsGrid } from './StatsGrid';
import { ChartRenderer } from './ChartRenderer';
import { TableRenderer } from './TableRenderer';

const MotionBox = motion(Box);

const renderBlock = (block, idx, data) => {
    if (block.type === 'statCards') return <StatsGrid key={idx} items={block.items} />;
    if (block.type === 'chart') return <ChartRenderer key={idx} block={block} data={data} />;
    if (block.type === 'table') return <TableRenderer key={idx} block={block} data={data} />;
    return null;
};

export const ChatMessage = ({ message }) => {
    const { renderSpec, warnings, followups } = message;

    return (
        <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            display="flex"
            justifyContent={message.type === 'user' ? 'flex-end' : 'flex-start'}
        >
            <Box
                maxW={message.type === 'user' ? 'md' : '80%'}
                w={message.type === 'ai' ? 'full' : undefined}
            >
                <Box
                    p={3}
                    borderRadius="lg"
                    bg={message.type === 'user' ? 'blue.50' : 'white'}
                    borderWidth="1px"
                    borderColor={message.type === 'user' ? 'var(--brand-primary)' : 'gray.200'}
                >
                    <Text fontSize="sm" color={message.type === 'user' ? 'blue.900' : 'gray.800'} mb={1} lineHeight={1.6}>
                        {message.content}
                    </Text>
                    <Text fontSize="2xs" color={message.type === 'user' ? 'blue.400' : 'gray.400'}>
                        {message.timestamp}
                    </Text>

                    {renderSpec && (
                        <VStack align="stretch" gap={3} mt={2}>
                            {/* Warning banner */}
                            {(warnings?.length > 0 || renderSpec._fallback) && (
                                <Alert status="warning" borderRadius="md" fontSize="xs">
                                    <AlertIcon />
                                    {warnings?.join(', ') || 'Data unavailable'}
                                </Alert>
                            )}

                            {/* Title + subtitle */}
                            <Box>
                                <Text fontWeight="bold" fontSize="sm" color="gray.900">
                                    {renderSpec.title}
                                </Text>
                                {renderSpec.subtitle && (
                                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                                        {renderSpec.subtitle}
                                    </Text>
                                )}
                            </Box>

                            {/* Blocks */}
                            {renderSpec.blocks?.map((block, idx) =>
                                renderBlock(block, idx, renderSpec.data)
                            )}

                            {/* Narrative */}
                            {renderSpec.narrative && (
                                <Box bg="blue.50" borderLeftWidth="3px" borderLeftColor="var(--brand-primary)" px={3} py={2.5} borderRadius="md">
                                    <Text fontSize="xs" color="gray.800" fontWeight="medium" mb={1.5} lineHeight={1.6}>
                                        {renderSpec.narrative.summary}
                                    </Text>
                                    {renderSpec.narrative.highlights?.map((h, i) => (
                                        <Text key={i} fontSize="xs" color="gray.600" lineHeight={1.6}>
                                            • {h}
                                        </Text>
                                    ))}
                                </Box>
                            )}

                            {/* Followup chips */}
                            {followups?.length > 0 && (
                                <HStack flexWrap="wrap" gap={2}>
                                    {followups.map((f, i) => (
                                        <Tag
                                            key={i} size="sm" cursor="pointer"
                                            bg="white" color="var(--brand-primary)"
                                            borderWidth="1px" borderColor="var(--brand-primary)"
                                            borderRadius="full" fontSize="xs"
                                            _hover={{ bg: 'blue.50' }}
                                        >
                                            {typeof f === 'string' ? f : f.question}
                                        </Tag>
                                    ))}
                                </HStack>
                            )}
                        </VStack>
                    )}
                </Box>
            </Box>
        </MotionBox>
    );
};
